import { isRejectedWithValue } from '@reduxjs/toolkit';
import type { Middleware } from '@reduxjs/toolkit';
import sentry from '../../../sentry';

const EXPECTED_STATUSES = new Set([
  400, // malformed/rejected request — shown as a form error
  401, // not signed in / session expired — handled by the refresh + logout flow
  402, // payment required — billing state, shown in the UI
  403, // insufficient permissions — expected for restricted accounts
  404, // missing or deleted resource — expected from stale links
  409, // conflict, e.g. duplicate — shown to the user
  410, // gone — same as 404 for our purposes
  422, // validation failure — shown as field errors
  423, // locked, e.g. MFA attempts exhausted (see MfaEnterCode)
  429, // rate limited — expected under load, not a code defect
]);

const isActionable = (payload: unknown): boolean => {
  const status = (payload as { status?: unknown } | null)?.status;

  if (typeof status === 'number') return !EXPECTED_STATUSES.has(status);

  return typeof navigator === 'undefined' || navigator.onLine !== false;
};

// Mutations handled by dashboardMutation — it sends structured Sentry events
// with dashboard_action/failure_class tags. Skip them here to avoid duplicates.
const DASHBOARD_MUTATION_ENDPOINTS = new Set([
  'createPassTemplate',
  'updatePassTemplate',
  'publishPassTemplate',
  'createPass',
  'updatePass',
]);

export const rtkQueryErrorLogger: Middleware = () => (next) => (action) => {
  if (isRejectedWithValue(action) && isActionable(action.payload)) {
    const endpoint = (action as { meta?: { arg?: { endpointName?: string } } })
      ?.meta?.arg?.endpointName;
    if (!DASHBOARD_MUTATION_ENDPOINTS.has(endpoint ?? '')) {
      sentry.sendError(action.payload);
    }
  }
  return next(action);
};
